/** Admin settings page with tab deeplinking via URL search params */

import { Link, useSearchParams } from 'react-router-dom'
import {
  ArrowLeft,
  Users,
  Building2,
  Key,
  Keyboard,
  HardDrive,
  Sparkles,
  Download,
} from 'lucide-react'
import { useAppStore } from '../stores/app-store'
import { cn } from '../lib/utils'
import { MembersTab } from '../components/settings/members-tab'
import { ApiKeysTab } from '../components/settings/api-keys-tab'
import { StorageTab } from '../components/settings/storage-tab'
import { AISettingsTab } from '../components/settings/ai-settings-tab'
import { ImportTab } from '../components/settings/import-tab'
import { ShortcutsTab } from '../components/settings/shortcuts-tab'
import { WorkspaceSettingsTab } from '../components/settings/workspace-settings-tab'
import { getWebFeatureProfile } from '../lib/feature-flags'
import { useAuth } from '../hooks/use-auth'

type TabId = 'members' | 'workspace' | 'api-keys' | 'ai' | 'storage' | 'import' | 'shortcuts'

const TABS: { id: TabId; label: string; icon: typeof Users }[] = [
  { id: 'members', label: 'Members', icon: Users },
  { id: 'workspace', label: 'Workspace', icon: Building2 },
  { id: 'api-keys', label: 'API Keys', icon: Key },
  { id: 'ai', label: 'AI', icon: Sparkles },
  { id: 'storage', label: 'Storage', icon: HardDrive },
  { id: 'import', label: 'Import', icon: Download },
  { id: 'shortcuts', label: 'Shortcuts', icon: Keyboard },
]

export function getVisibleSettingsTabIds(storageEnabled: boolean, isAdmin: boolean): TabId[] {
  if (!isAdmin) return ['shortcuts']
  return TABS.filter((tab) => storageEnabled || (tab.id !== 'storage' && tab.id !== 'import')).map(
    (tab) => tab.id,
  )
}

export function SettingsPage() {
  const storageEnabled = getWebFeatureProfile().storageSpace
  const { role } = useAuth()
  const visibleTabIds = getVisibleSettingsTabIds(storageEnabled, role === 'admin')
  const visibleTabs = TABS.filter((tab) => visibleTabIds.includes(tab.id))
  const [searchParams, setSearchParams] = useSearchParams()
  const { theme } = useAppStore()
  const isDark = theme === 'dark'

  // Derive active tab from URL — fallback to the first role-visible tab if invalid.
  const rawTab = searchParams.get('tab')
  const activeTab: TabId = visibleTabIds.includes(rawTab as TabId)
    ? (rawTab as TabId)
    : visibleTabIds[0]

  const setTab = (id: TabId) => setSearchParams({ tab: id }, { replace: true })
  const moveTab = (id: TabId, key: string) => {
    const index = visibleTabIds.indexOf(id)
    const nextIndex =
      key === 'Home'
        ? 0
        : key === 'End'
          ? visibleTabIds.length - 1
          : (index + (key === 'ArrowLeft' ? -1 : 1) + visibleTabIds.length) % visibleTabIds.length
    const next = visibleTabIds[nextIndex]
    document.getElementById(`settings-tab-${next}`)?.focus()
    setTab(next)
  }

  return (
    <div className={cn('min-h-screen', isDark ? 'bg-surface-0' : 'bg-neutral-50')}>
      {/* Header */}
      <div
        className={cn(
          'border-b px-4 py-4 md:px-6',
          isDark ? 'border-white/[0.06]' : 'border-neutral-200',
        )}
      >
        <div className="mx-auto flex max-w-3xl items-center gap-3">
          <Link
            to="/"
            aria-label="Back to Home"
            className={cn(
              'cursor-pointer rounded-lg p-2 md:p-1.5',
              isDark
                ? 'hover:bg-surface-3 active:bg-surface-3 text-neutral-400'
                : 'hover:bg-neutral-100 active:bg-neutral-100 text-neutral-500',
            )}
          >
            <ArrowLeft className="h-5 w-5 md:h-4 md:w-4" />
          </Link>
          <h1
            className={cn(
              'text-lg font-semibold',
              isDark ? 'text-neutral-100' : 'text-neutral-900',
            )}
          >
            Settings
          </h1>
        </div>
      </div>

      <div className="mx-auto max-w-3xl px-4 py-4 md:px-6 md:py-6">
        {/* Tab nav — scrollable on mobile, deeplinked via ?tab=<id> */}
        <div
          role="tablist"
          aria-label="Settings sections"
          className={cn(
            'flex gap-1 rounded-lg border p-1 mb-6 overflow-x-auto',
            isDark ? 'border-white/[0.06] bg-surface-1' : 'border-neutral-200 bg-neutral-100',
          )}
        >
          {visibleTabs.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              id={`settings-tab-${id}`}
              role="tab"
              aria-selected={activeTab === id}
              aria-controls={`settings-panel-${id}`}
              tabIndex={activeTab === id ? 0 : -1}
              onClick={() => setTab(id)}
              onKeyDown={(event) => {
                if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return
                event.preventDefault()
                moveTab(id, event.key)
              }}
              className={cn(
                'flex shrink-0 flex-1 cursor-pointer items-center justify-center gap-1.5 rounded-md px-3 py-2 text-xs font-medium transition-colors md:py-1.5',
                activeTab === id
                  ? 'bg-brand-600 text-white'
                  : isDark
                    ? 'text-neutral-400 hover:text-neutral-200'
                    : 'text-neutral-500 hover:text-neutral-700',
              )}
            >
              <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              <span className="hidden sm:inline">{label}</span>
              <span className="sr-only sm:hidden">{label}</span>
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div
          id={`settings-panel-${activeTab}`}
          role="tabpanel"
          aria-labelledby={`settings-tab-${activeTab}`}
          tabIndex={0}
        >
          {activeTab === 'members' && <MembersTab isDark={isDark} />}
          {activeTab === 'workspace' && <WorkspaceSettingsTab isDark={isDark} />}
          {activeTab === 'api-keys' && <ApiKeysTab isDark={isDark} />}
          {activeTab === 'ai' && <AISettingsTab isDark={isDark} />}
          {storageEnabled && activeTab === 'storage' && <StorageTab isDark={isDark} />}
          {storageEnabled && activeTab === 'import' && <ImportTab isDark={isDark} />}
          {activeTab === 'shortcuts' && <ShortcutsTab isDark={isDark} />}
        </div>
      </div>
    </div>
  )
}
